import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Eye, EyeOff, Palette, ShoppingBag } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';

export default function Register() {
  const navigate = useNavigate();
  const { register, isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: '',
    bio: '',
  });

  useEffect(() => {
    if (isAuthenticated) navigate('/');
  }, [isAuthenticated, navigate]);

  if (isAuthenticated) return null;

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleRoleSelect = (role) => {
    setFormData(prev => ({ ...prev, role }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.password || !formData.role) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    try {
      setLoading(true);
      await register(formData);
      toast.success('Welcome to Artisan Gallery!');
      
      if (formData.role === 'artist') {
        navigate('/upload');
      } else {
        navigate('/membership');
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Registration failed';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-16 px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-12">
          <Link to="/" className="font-serif text-2xl tracking-tight text-[#F5F5F0]">
            Mortal<span className="text-[#D4AF37]">Works</span>
          </Link>
          <h1 className="font-serif text-3xl text-[#F5F5F0] mt-8">Join Our Community</h1>
          <p className="text-[#71717A] mt-2">Create your account</p>
        </div>

        {/* Role Selection */}
        {!formData.role ? (
          <div className="space-y-4">
            <p className="text-center text-[#A1A1AA] mb-6">I want to...</p>
            
            <button
              onClick={() => handleRoleSelect('artist')}
              className="w-full p-6 bg-[#121212] border border-white/10 hover:border-[#D4AF37]/50 transition-colors text-left group"
              data-testid="role-artist-btn"
            >
              <div className="flex items-center gap-4">
                <Palette className="text-[#D4AF37]" size={32} />
                <div>
                  <h3 className="text-[#F5F5F0] font-medium group-hover:text-[#D4AF37] transition-colors">
                    Sell My Art
                  </h3>
                  <p className="text-[#71717A] text-sm mt-1">
                    Share your photography, paintings, music, poetry & more
                  </p>
                </div>
              </div>
              <div className="mt-4 text-xs text-[#71717A]">
                $3 listing fee • 18% commission • Keep 82% of sales
              </div>
            </button>
            
            <button
              onClick={() => handleRoleSelect('buyer')}
              className="w-full p-6 bg-[#121212] border border-white/10 hover:border-[#D4AF37]/50 transition-colors text-left group"
              data-testid="role-buyer-btn"
            >
              <div className="flex items-center gap-4">
                <ShoppingBag className="text-[#D4AF37]" size={32} />
                <div>
                  <h3 className="text-[#F5F5F0] font-medium group-hover:text-[#D4AF37] transition-colors">
                    Collect Art
                  </h3>
                  <p className="text-[#71717A] text-sm mt-1">
                    Discover and purchase authentic human-created artwork
                  </p>
                </div>
              </div>
              <div className="mt-4 text-xs text-[#71717A]">
                $5/month membership • Full access to gallery
              </div>
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="flex items-center justify-between mb-6">
              <span className="badge badge-gold">
                {formData.role === 'artist' ? 'Artist Account' : 'Collector Account'}
              </span>
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, role: '' }))}
                className="text-[#71717A] text-sm hover:text-[#F5F5F0]"
              >
                Change
              </button>
            </div>

            <div className="form-group">
              <label className="form-label">Name *</label>
              <Input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Your name"
                className="form-input"
                data-testid="name-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Email *</label>
              <Input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="you@example.com"
                className="form-input"
                data-testid="email-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Password *</label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="At least 6 characters"
                  className="form-input pr-12"
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#71717A] hover:text-[#F5F5F0]"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {formData.role === 'artist' && (
              <div className="form-group">
                <label className="form-label">Bio (Optional)</label>
                <Textarea
                  name="bio"
                  value={formData.bio}
                  onChange={handleChange}
                  placeholder="Tell us about yourself and your art..."
                  rows={3}
                  className="form-input resize-none"
                  data-testid="bio-input"
                />
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="btn-primary w-full"
              data-testid="register-submit-btn"
            >
              {loading ? 'Creating account...' : 'Create Account'}
            </Button>
          </form>
        )}

        <p className="text-center mt-8 text-[#71717A]">
          Already have an account?{' '}
          <Link to="/login" className="text-[#D4AF37] hover:text-[#F5F5F0] transition-colors">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
